import { useState, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { errorMessage } from '../lib/errors'

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost'

export function Button({ variant = 'primary', busy, children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; busy?: boolean }) {
  return (
    <button {...rest} className={`btn btn-${variant} ${rest.className ?? ''}`} disabled={rest.disabled || busy} aria-busy={busy}>
      {busy ? <span className="spinner" aria-hidden /> : null}
      {children}
    </button>
  )
}

export function Card({ title, actions, children, className }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`card ${className ?? ''}`}>
      {(title || actions) && (
        <header className="card-header">
          {title ? <h2>{title}</h2> : <span />}
          {actions ? <div className="card-actions">{actions}</div> : null}
        </header>
      )}
      {children}
    </section>
  )
}

export function Badge({ tone = 'neutral', children }: { tone?: 'neutral' | 'ok' | 'warn' | 'bad' | 'info'; children: ReactNode }) {
  return <span className={`badge badge-${tone}`}>{children}</span>
}

const TONES: Record<string, 'neutral' | 'ok' | 'warn' | 'bad' | 'info'> = {
  completed: 'ok', approved: 'ok', paid: 'ok', available: 'ok', resolved: 'ok', active: 'ok', settled: 'ok',
  pending: 'warn', searching: 'warn', open: 'warn', draft: 'warn', accrued: 'warn',
  in_progress: 'info', assigned: 'info', driver_en_route: 'info', driver_arrived: 'info', on_trip: 'info',
  cancelled: 'bad', rejected: 'bad', suspended: 'bad', failed: 'bad', expired: 'bad', disputed: 'bad', void: 'bad',
}
export function StatusBadge({ value, labels }: { value: string | null | undefined; labels: Record<string, string> }) {
  if (!value) return <Badge>—</Badge>
  return <Badge tone={TONES[value] ?? 'neutral'}>{labels[value] ?? value}</Badge>
}

export function Loading({ label = 'Cargando…' }: { label?: string }) {
  return (
    <div className="state" role="status">
      <span className="spinner" aria-hidden /> {label}
    </div>
  )
}

export function ErrorBox({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="state state-error" role="alert">
      <strong>No se pudo completar.</strong> <span>{message}</span>
      {onRetry ? <Button variant="secondary" onClick={onRetry}>Reintentar</Button> : null}
    </div>
  )
}

export function Empty({ children = 'No hay resultados para los filtros seleccionados.' }: { children?: ReactNode }) {
  return <div className="state state-empty">{children}</div>
}

export function Modal({ title, onClose, children, footer }: { title: string; onClose(): void; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={title}>
        <header className="modal-header">
          <h3>{title}</h3>
          <button className="icon-btn" onClick={onClose} aria-label="Cerrar">×</button>
        </header>
        <div className="modal-body">{children}</div>
        {footer ? <footer className="modal-footer">{footer}</footer> : null}
      </div>
    </div>
  )
}

/**
 * Confirmation dialog for important actions. A reason (≥ 5 chars) is mandatory because the
 * server requires it and stores it in the audit log.
 */
export function ConfirmAction({
  title, description, confirmLabel, danger, requireReason = true, onConfirm, onClose, children,
}: {
  title: string
  description?: ReactNode
  confirmLabel: string
  danger?: boolean
  requireReason?: boolean
  onConfirm(reason: string): Promise<void>
  onClose(): void
  children?: ReactNode
}) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const valid = !requireReason || reason.trim().length >= 5
  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            busy={busy}
            disabled={!valid}
            onClick={async () => {
              setBusy(true)
              setError(null)
              try {
                await onConfirm(reason.trim())
                onClose()
              } catch (e) {
                setError(errorMessage(e))
              } finally {
                setBusy(false)
              }
            }}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {description ? <p className="muted">{description}</p> : null}
      {children}
      {requireReason ? (
        <label className="field">
          <span>Motivo (queda registrado en la auditoría)</span>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={500} autoFocus />
          {!valid && reason.length > 0 ? <small className="hint-error">Mínimo 5 caracteres.</small> : null}
        </label>
      ) : null}
      {error ? <div className="state state-error" role="alert">{error}</div> : null}
    </Modal>
  )
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: ReactNode }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
      {hint ? <small className="muted">{hint}</small> : null}
    </label>
  )
}

export function Stat({ label, value, hint, tone }: { label: string; value: ReactNode; hint?: string; tone?: 'accent' | 'ok' | 'warn' }) {
  return (
    <div className={`stat ${tone ? `stat-${tone}` : ''}`}>
      <span className="stat-label">{label}</span>
      <strong className="stat-value">{value}</strong>
      {hint ? <span className="stat-hint">{hint}</span> : null}
    </div>
  )
}
